import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IonContent,
  IonPage,
  IonText,
  IonSpinner,
  IonButton,
  IonIcon,
  IonDatetime,
} from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import moment from "moment";
import Header from "src/components/Header";
import MisAsignacionesService, {
  MisAsignacionesResponse,
} from "src/services/misAsignaciones";
import {
  Caso,
  CasoCard,
  CasoDetalleModal,
  DIA_KEYS,
  GARNET,
  emptyData,
  filtrarCasosPorFecha,
} from "src/components/miDia/misCasos";

const MisAsignaciones: React.FC = () => {
  const [data, setData] = useState<MisAsignacionesResponse>(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fecha, setFecha] = useState<Date>(new Date());
  const [casoDetalle, setCasoDetalle] = useState<Caso | null>(null);

  const cargar = useCallback(async (fechaSel: Date) => {
    setLoading(true);
    setError(null);
    try {
      const f = moment(fechaSel).format("YYYY-MM-DD");
      const response = await MisAsignacionesService.listar(f);
      setData({ ...emptyData, ...response });
    } catch (err) {
      console.error("Error al cargar mis asignaciones:", err);
      setError("No se pudieron cargar tus casos.");
      setData(emptyData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar(fecha);
  }, [cargar, fecha]);

  const fechaMoment = useMemo(() => moment(fecha), [fecha]);
  const esHoy = fechaMoment.isSame(moment(), "day");
  const keyDia = DIA_KEYS[fechaMoment.day()];
  const diaLabel = esHoy
    ? "Hoy"
    : fechaMoment.locale("es-mx").format("ddd DD/MM");

  const casos = useMemo<Caso[]>(
    () => filtrarCasosPorFecha(data, fechaMoment),
    [data, fechaMoment]
  );

  const onEstadoCambiado = useCallback(
    (confirmacionId: number, estado: string) => {
      setData((prev) => ({
        ...prev,
        reemplazos: prev.reemplazos.map((r: any) =>
          r.confirmacion_id === confirmacionId
            ? { ...r, estado_confirmacion: estado }
            : r
        ),
      }));
      setCasoDetalle((prev) =>
        prev && prev.item?.confirmacion_id === confirmacionId
          ? { ...prev, item: { ...prev.item, estado_confirmacion: estado } }
          : prev
      );
    },
    []
  );

  const irDia = (delta: number) =>
    setFecha(moment(fecha).add(delta, "day").toDate());

  return (
    <IonPage>
      <Header titulo="Mi dia" />
      <IonContent id="mis-asignaciones" style={{ "--background": "#f4f5f8" } as any}>
        <div style={{ padding: 16 }}>
          {/* Selector de fecha */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 14,
              boxShadow: "0 2px 8px rgba(0,0,0,.06)",
              padding: "6px 8px",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <IonButton
              fill="clear"
              onClick={() => irDia(-1)}
              style={{ "--color": GARNET } as any}
            >
              <IonIcon slot="icon-only" icon={chevronBackOutline} />
            </IonButton>
            <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
              <IonDatetime
                value={fechaMoment.format("YYYY-MM-DD")}
                displayFormat="DD/MM/YYYY"
                cancelText="Cancelar"
                doneText="OK"
                onIonChange={(e) => {
                  const v = e.detail.value;
                  if (!v) return;
                  const nueva = moment(v);
                  if (nueva.isValid() && !nueva.isSame(fechaMoment, "day")) {
                    setFecha(nueva.toDate());
                  }
                }}
                style={{
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: 15,
                  "--padding-start": "0px",
                }}
              />
            </div>
            <IonButton
              fill="clear"
              onClick={() => irDia(1)}
              style={{ "--color": GARNET } as any}
            >
              <IonIcon slot="icon-only" icon={chevronForwardOutline} />
            </IonButton>
          </div>

          <div
            style={{
              marginBottom: 12,
              textTransform: "capitalize",
              textAlign: "center",
            }}
          >
            <IonText color="medium">
              <small>{fechaMoment.locale("es-mx").format("dddd DD/MM/YYYY")}</small>
            </IonText>
          </div>

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <IonSpinner />
            </div>
          ) : error ? (
            <IonText color="danger">{error}</IonText>
          ) : casos.length === 0 ? (
            <IonText color="medium">
              {esHoy
                ? "No tenes casos para hoy."
                : "No tenes casos para esa fecha."}
            </IonText>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {casos.map((caso, idx) => (
                <CasoCard
                  key={`${caso.tipo}-${idx}`}
                  caso={caso}
                  keyDia={keyDia}
                  onClick={() => setCasoDetalle(caso)}
                />
              ))}
            </div>
          )}
        </div>

        <CasoDetalleModal
          caso={casoDetalle}
          keyDia={keyDia}
          esHoy={esHoy}
          diaLabel={diaLabel}
          onClose={() => setCasoDetalle(null)}
          onEstadoCambiado={onEstadoCambiado}
        />
      </IonContent>
    </IonPage>
  );
};

export default MisAsignaciones;
